import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MultiSelectChips from '@/components/MultiSelectChips';
import { NIGERIA_REGIONS, statesForRegions, COUNTRIES_WITH_REGIONS } from '@/data/nigeriaRegions';

// Countries offered for a distributor's coverage. Nigeria is the launch
// country and the only one with a region breakdown so far; other
// countries are saved as the country alone.
export const COVERAGE_COUNTRIES = [
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'UG', name: 'Uganda' },
  { code: 'TZ', name: 'Tanzania' },
];

/** The coverage is complete: a country, and for Nigeria at least one
 * state (regions only help pick the states). */
export function coverageComplete({ country, states }) {
  if (!country) return false;
  if (!COUNTRIES_WITH_REGIONS.has(country)) return true;
  return states.length > 0;
}

/**
 * Where a distributor sells: country, then (Nigeria) the regions and the
 * states within them. Used by the distributor's first-time wizard (PR 73)
 * and edit profile. Regions narrow the state list; removing a region
 * drops its states.
 */
export default function CoverageFields({ value, onChange, testPrefix = 'coverage' }) {
  const { t } = useTranslation();
  const { country, regions, states } = value;
  const set = (fields) => onChange({ ...value, ...fields });
  const hasRegions = COUNTRIES_WITH_REGIONS.has(country);
  const stateOptions = statesForRegions(regions).map((s) => ({ value: s, label: s }));

  useEffect(() => {
    const allowed = new Set(statesForRegions(regions));
    if (states.some((s) => !allowed.has(s))) set({ states: states.filter((s) => allowed.has(s)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className="block text-base font-semibold text-gray-900">{t('auth.register.coverage.country')}</label>
        <Select value={country} onValueChange={(v) => set({ country: v, regions: [], states: [] })}>
          <SelectTrigger className="h-14 bg-white text-base" data-testid={`${testPrefix}-country`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {COVERAGE_COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.code} className="text-base py-3">{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {hasRegions ? (
        <>
          <div className="space-y-2">
            <label className="block text-base font-semibold text-gray-900">{t('auth.register.coverage.regions')}</label>
            <MultiSelectChips
              options={NIGERIA_REGIONS.map((r) => ({ value: r.id, label: t(r.labelKey) }))}
              value={regions}
              onChange={(v) => set({ regions: v })}
              placeholder={t('auth.register.coverage.regionsPlaceholder')}
              searchPlaceholder={t('auth.register.coverage.regionsSearch')}
              emptyText={t('auth.register.coverage.regionsEmpty')}
              testId={`${testPrefix}-regions`}
            />
          </div>
          <div className="space-y-2">
            <label className="block text-base font-semibold text-gray-900">{t('auth.register.coverage.states')}</label>
            <MultiSelectChips
              options={stateOptions}
              value={states}
              onChange={(v) => set({ states: v })}
              placeholder={regions.length === 0
                ? t('auth.register.coverage.statesPickRegionFirst')
                : t('auth.register.coverage.statesPlaceholder')}
              searchPlaceholder={t('auth.register.coverage.statesSearch')}
              emptyText={t('auth.register.coverage.statesEmpty')}
              disabled={regions.length === 0}
              testId={`${testPrefix}-states`}
            />
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">{t('auth.register.coverage.noRegionData')}</p>
      )}
    </div>
  );
}
